import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { useWpSetMaxNumberOfOptionsMutation } from './WarrantProgramStepper.graphql';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { useEffect } from 'react';

type FormValues = { maxNumberOfOptions: string };

export const WpStepMaxNumberOfOptions = ({
  warrantProgram,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'maxNumberOfOptions' | 'companyId'>;
}) => {
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const [doSubmit, { loading }] = useWpSetMaxNumberOfOptionsMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { maxNumberOfOptions: warrantProgram.maxNumberOfOptions ?? '' },
  });

  useEffect(() => {
    reset({ maxNumberOfOptions: warrantProgram.maxNumberOfOptions ?? '' });
  }, [warrantProgram.maxNumberOfOptions, reset]);

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSubmit({
        variables: {
          companyId: warrantProgram.companyId,
          input: { id: warrantProgram.id, maxNumberOfOptions: values.maxNumberOfOptions },
        },
      });
    } catch (e) {
      captureException(e);
      toast({
        title: <Trans id="Could not save" />,
        severity: 'danger',
        description: (
          <Trans id="Something wen wrong when saving, please try again and contact support if problem persists" />
        ),
      });
      return;
    }
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Max number of options?" />
        </Typography>
        <Alert>
          <Trans id="The total amount of options which can be issued in the program. This is the sum of all individual allocations of options." />
        </Alert>

        <Box maxWidth={300}>
          <Controller
            control={control}
            name="maxNumberOfOptions"
            render={({ field }) => (
              <FormControl required>
                <FormLabel>
                  <Trans id="Max number of options" />
                </FormLabel>
                <NumericFormatInput {...field} lang={i18n.locale} />
              </FormControl>
            )}
          />
        </Box>

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
